import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { LuRefreshCw, LuUpload } from 'react-icons/lu';
import { Button } from 'components/common';
import DocumentAttachment from 'components/common/upload/DocumentAttachment';
import { CRM_CONTRACT_ATTACHENTS_TYPE } from 'app_config';

const EXTRACTION_TEXT = {
  pending:      { text: 'Waiting for AI…',  cls: 'tw-text-slate-500' },
  processing:   { text: 'AI is reading…',   cls: 'tw-text-primary-600' },
  completed:    { text: 'Read by AI',       cls: 'tw-text-emerald-700' },
  failed:       { text: 'AI failed',        cls: 'tw-text-red-600' },
  canceled:     { text: 'Canceled',         cls: 'tw-text-slate-500' },
  skipped:      { text: 'Skipped',          cls: 'tw-text-amber-700' },
  needs_review: { text: 'Unreadable scan',  cls: 'tw-text-amber-700' },
};

const ExtractionLine = ({ extraction }) => {
  if (!extraction) return <span className="tw-text-[11px] tw-text-slate-400">Not read by AI yet</span>;

  const meta = EXTRACTION_TEXT[extraction.status] || { text: extraction.status, cls: 'tw-text-slate-500' };
  const running = ['pending', 'processing'].includes(extraction.status);
  return (
    <span className={`tw-text-[11px] tw-font-medium ${meta.cls}`} title={extraction.error_message || undefined}>
      {meta.text}
      {running && extraction.progress > 0 && ` ${extraction.progress}%`}
      {extraction.status === 'completed' && ` · ${extraction.buildings_found} building${extraction.buildings_found === 1 ? '' : 's'} found`}
      {extraction.error_message && !running && ` — ${extraction.error_message}`}
    </span>
  );
};
ExtractionLine.propTypes = { extraction: PropTypes.object };

// Step 2 body: the Proposal Sales sent back (Word / PDF). Uploading triggers the
// AI read; step 3 fills in as soon as it completes. The step frame owns the title.
const ProposalFilesCard = ({ documents, extractions, canEdit, busy, onUpload, onRemove, onReextract }) => {
  const [uploading, setUploading] = useState(false);
  const extractionByBlob = Object.fromEntries((extractions || []).map((e) => [e.blob_id, e]));
  const aiRunning = (extractions || []).some((e) => ['pending', 'processing'].includes(e.status));

  const onDrop = useCallback(async (files) => {
    if (!files.length) return;
    setUploading(true);
    try {
      await onUpload(files);
    } finally {
      setUploading(false);
    }
  }, [onUpload]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: CRM_CONTRACT_ATTACHENTS_TYPE,
    multiple: true,
    disabled: !canEdit || uploading || busy,
  });

  return (
    <div>
      {canEdit && documents.length > 0 && (
        <div className="tw-flex tw-justify-end tw-px-4 tw-pt-3">
          <Button type="button" variant="secondary" size="xs" leftIcon={LuRefreshCw}
            onClick={() => onReextract()} disabled={busy || aiRunning}
            title={aiRunning ? 'AI is still reading — wait for it to finish' : 'Read the uploaded proposal file(s) again'}>
            Re-run AI
          </Button>
        </div>
      )}

      {documents.length > 0 && (
        <ul className="tw-list-none tw-m-0 tw-p-0">
          {documents.map((doc) => (
            <li key={doc.id} className="tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-2 tw-border-0 tw-border-b tw-border-solid tw-border-slate-100">
              <div className="tw-flex-1 tw-min-w-0">
                <DocumentAttachment {...doc} isTable allowView allowDownload
                  allowRemove={canEdit} handleRemoveAttachment={() => onRemove(doc)} />
              </div>
              <ExtractionLine extraction={extractionByBlob[doc.blob_id]} />
            </li>
          ))}
        </ul>
      )}

      {canEdit ? (
        <div {...getRootProps()}
          className={`tw-m-3 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-4 tw-rounded-md tw-border tw-border-dashed tw-text-xs tw-cursor-pointer ${
            isDragActive ? 'tw-border-primary-400 tw-bg-primary-50 tw-text-primary-700' : 'tw-border-slate-300 tw-text-slate-500 hover:tw-bg-slate-50'}`}>
          <input {...getInputProps()} />
          <LuUpload size={14} />
          {uploading ? 'Uploading…' : 'Drop the Proposal (Word or PDF) here, or click to choose a file'}
        </div>
      ) : documents.length === 0 && (
        <p className="tw-px-4 tw-py-4 tw-text-xs tw-text-slate-400 tw-mb-0">No proposal uploaded.</p>
      )}
    </div>
  );
};

ProposalFilesCard.propTypes = {
  documents:   PropTypes.array.isRequired,
  extractions: PropTypes.array,
  canEdit:     PropTypes.bool,
  busy:        PropTypes.bool,
  onUpload:    PropTypes.func.isRequired,
  onRemove:    PropTypes.func.isRequired,
  onReextract: PropTypes.func.isRequired,
};

export default ProposalFilesCard;
